import { LockOutlined } from '@ant-design/icons';
import { useIntl, useModel } from '@umijs/max';
import { App, Form, Input, Modal } from 'antd';
import React, { useState } from 'react';
import { changePassword } from '@/services/ant-design-pro/api';

type ChangePasswordModalProps = {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  title?: string;
};

type FormValues = API.ChangePasswordParams & {
  confirmPassword: string;
  username?: string;
};

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,20}$/;

const isSuccessfulResult = (result: API.CommonResult) => {
  if (result.success === true) {
    return true;
  }
  if (typeof result.status === 'string') {
    return ['success', 'ok'].includes(result.status.toLowerCase());
  }
  return false;
};

const getErrorMessage = (error: any, fallback: string) => {
  return (
    error?.info?.errorMessage ||
    error?.response?.data?.errorMessage ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onCancel,
  onSuccess,
  title,
}) => {
  const intl = useIntl();
  const { initialState } = useModel('@@initialState');
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const username =
    initialState?.currentUser?.userName ||
    initialState?.currentUser?.name ||
    '';

  const modalTitle =
    title ||
    intl.formatMessage({
      id: 'app.settings.security.change-password',
      defaultMessage: '修改密码',
    });

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const result = await changePassword(
        {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        },
        {
          skipErrorHandler: true,
        },
      );

      if (!isSuccessfulResult(result)) {
        const errorMessage =
          result.message ||
          intl.formatMessage({
            id: 'app.settings.security.change-password-failed',
            defaultMessage: '密码修改失败',
          });
        Modal.error({
          title: intl.formatMessage({
            id: 'app.settings.security.change-password-failed',
            defaultMessage: '密码修改失败',
          }),
          content: errorMessage,
        });
        return;
      }

      message.success(
        result.message ||
          intl.formatMessage({
            id: 'app.settings.security.change-password-success',
            defaultMessage: '密码修改成功',
          }),
      );
      form.resetFields();
      onSuccess?.();
      onCancel();
    } catch (error) {
      message.error(
        getErrorMessage(
          error,
          intl.formatMessage({
            id: 'app.settings.security.change-password-failed',
            defaultMessage: '密码修改失败',
          }),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      afterOpenChange={(visible) => {
        if (!visible) {
          form.resetFields();
          setSubmitting(false);
        }
      }}
      destroyOnHidden
      forceRender
      open={open}
      title={modalTitle}
      okText={intl.formatMessage({
        id: 'app.settings.security.submit',
        defaultMessage: '确认修改',
      })}
      cancelText={intl.formatMessage({
        id: 'app.settings.security.cancel',
        defaultMessage: '取消',
      })}
      confirmLoading={submitting}
      onOk={handleSubmit}
      onCancel={onCancel}
    >
      <Form<FormValues> form={form} layout="vertical" requiredMark={false}>
        <Form.Item hidden initialValue={username} name="username">
          <Input autoComplete="username" />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({
            id: 'app.settings.security.old-password',
            defaultMessage: '当前密码',
          })}
          name="oldPassword"
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'app.settings.security.old-password-required',
                defaultMessage: '请输入当前密码',
              }),
            },
          ]}
        >
          <Input.Password
            autoComplete="current-password"
            prefix={<LockOutlined />}
            placeholder={intl.formatMessage({
              id: 'app.settings.security.old-password-placeholder',
              defaultMessage: '请输入当前密码',
            })}
          />
        </Form.Item>
        <Form.Item
          extra={intl.formatMessage({
            id: 'app.settings.security.password-rule',
            defaultMessage: '8-20 位，包含大小写字母、数字和特殊字符 @$!%*?&',
          })}
          label={intl.formatMessage({
            id: 'app.settings.security.new-password',
            defaultMessage: '新密码',
          })}
          name="newPassword"
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'app.settings.security.new-password-required',
                defaultMessage: '请输入新密码',
              }),
            },
            {
              pattern: PASSWORD_PATTERN,
              message: intl.formatMessage({
                id: 'app.settings.security.password-rule',
                defaultMessage:
                  '8-20 位，包含大小写字母、数字和特殊字符 @$!%*?&',
              }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || value !== getFieldValue('oldPassword')) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: 'app.settings.security.password-different',
                      defaultMessage: '新密码不能与当前密码相同',
                    }),
                  ),
                );
              },
            }),
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            prefix={<LockOutlined />}
            placeholder={intl.formatMessage({
              id: 'app.settings.security.new-password-placeholder',
              defaultMessage: '请输入新密码',
            })}
          />
        </Form.Item>
        <Form.Item
          dependencies={['newPassword']}
          label={intl.formatMessage({
            id: 'app.settings.security.confirm-password',
            defaultMessage: '确认新密码',
          })}
          name="confirmPassword"
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'app.settings.security.confirm-password-required',
                defaultMessage: '请再次输入新密码',
              }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: 'app.settings.security.confirm-password-mismatch',
                      defaultMessage: '两次输入的新密码不一致',
                    }),
                  ),
                );
              },
            }),
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            prefix={<LockOutlined />}
            placeholder={intl.formatMessage({
              id: 'app.settings.security.confirm-password-placeholder',
              defaultMessage: '请再次输入新密码',
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
